import React, { useState, useEffect } from 'react';
import { TextField, Typography, Box } from '@mui/material';
import { GetUserUUID, Textarea, ImageUploader } from '@client/components';
import { PyrenzBlueButton } from '@client/theme';
import { usePyrenzAlert } from '@renderer/provider';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@client/store';
import { uploadImage, Utils } from '@client/utility';

export function Profile() {
  const [username, setUsername] = useState('');
  const [userUUID, setUserUUID] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  const showAlert = usePyrenzAlert();
  const navigate = useNavigate();
  const { setIncludeForbiddens } = useUserStore();

  useEffect(() => {
    const fetchUserData = async () => {
      const uuid = await GetUserUUID();
      setUserUUID(uuid);

      if (uuid) {
        const { data, error } = await Utils.db.client
          .from('user_data')
          .select('username, avatar_url, blocked_tags')
          .eq('user_uuid', uuid)
          .single();

        if (!error && data) {
          setUsername(data.username || '');
          setImageUrl(data.avatar_url);
          if (data.blocked_tags) setTagsInput(data.blocked_tags.join(', '));
        }
      }
    };

    fetchUserData();
  }, []);

  const handleImageSelect = async (file: File | null): Promise<void> => {
    if (!file) {
      showAlert('No file was selected', 'alert');
      return;
    }

    if (!file.type.match('image.*')) {
      showAlert('Only image files are accepted. Please upload an image.', 'alert');
      return;
    }

    setSelectedImage(file);
    setImageUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const updateData: {
        username: string;
        avatar_url?: string | null;
        blocked_tags?: string[];
      } = {
        username,
        avatar_url: imageUrl,
      };

      if (selectedImage) {
        const { url, error } = await uploadImage('user-profile', selectedImage);
        if (error) {
          showAlert(error, 'alert');
          return;
        }
        updateData.avatar_url = url;
      }

      const tagsArray = tagsInput
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag);

      updateData.blocked_tags = tagsArray;

      if (userUUID) {
        const { error } = await Utils.db.client
          .from('user_data')
          .update(updateData)
          .eq('user_uuid', userUUID);

        if (error) {
          showAlert(`Error updating profile: ${error.message}`, 'alert');
        } else {
          setIncludeForbiddens(true);
          showAlert('Profile and blocked tags updated successfully', 'success');
          navigate(`/Profile/${userUUID}`);
        }
      }
    } catch (error) {
      showAlert(
        `Error during submission: ${
          error instanceof Error ? error.message : String(error)
        }`,
        'alert'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ padding: '20px', maxWidth: '400px', margin: '0 auto' }}>
      <Box sx={{ marginBottom: '20px' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
          Profile Image
        </Typography>
        <ImageUploader onImageSelect={handleImageSelect} initialImage={imageUrl ?? undefined} />
      </Box>

      <Box sx={{ marginBottom: '20px' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
          Username
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
      </Box>

      <Box sx={{ marginBottom: '20px' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
          Blocked Tags
        </Typography>
        <Textarea
          placeholder="Enter tags separated by commas"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
        />
      </Box>

      <PyrenzBlueButton
        onClick={handleSubmit}
        disabled={isLoading}
        dataState={isLoading ? 'loading' : ''}
        sx={{ borderRadius: '20px', marginRight: '10px' }}
      >
        {isLoading ? 'Loading...' : 'Submit'}
      </PyrenzBlueButton>

      <PyrenzBlueButton
        onClick={() => userUUID && navigate(`/Profile/${userUUID}`)}
        sx={{ borderRadius: '20px' }}
      >
        View Profile
      </PyrenzBlueButton>
    </Box>
  );
}
